import { useState } from "react";
import {
  ActionIcon,
  Group,
  Select,
  Stack,
  Tooltip,
} from "@mantine/core";
import IconTrash from "@tabler/icons-react/dist/esm/icons/IconTrash.mjs";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";

import Condition from "./Condition";
import Placeholder from "../Placeholder/Placeholder";
import {
  useDeleteGroupMutation,
  useEditGroupMutation,
} from "../../hooks/mutations";
import { useSelectIsPlaceholderTarget } from "../../../../redux/features/queryBuilder/queryBuilderSlice";
import {
  DROP_CONDITION_GROUP,
  conditionGroupId as makeConditionGroupId,
} from "../../dnd/kinds";
import { DeleteConfirmModal } from "../../../DeleteConfirmationModal";
import classes from "./ConditionGroup.module.scss";

const AND_OR_ITEMS = ["And", "Or"];

/**
 * Recursive condition group. The whole group is a drop target: dropping an
 * entity-field here adds a new condition row (handled by the central drag
 * handler). isTopLevel controls header visibility.
 */
export default function ConditionGroup({
  ruleId,
  queryId,
  forJoin,
  conditionGroupData,
  isTopLevel = false,
  canEdit = true,
}) {
  const [deleteOpened, setDeleteOpened] = useState(false);
  const groupId = conditionGroupData?.id;

  const dropId = makeConditionGroupId({ queryId, groupId, forJoin });
  const { setNodeRef, isOver } = useDroppable({
    id: dropId,
    data: {
      kind: DROP_CONDITION_GROUP,
      queryId,
      groupId,
      forJoin,
    },
  });

  const placeholderViaSlice = useSelectIsPlaceholderTarget({
    queryId,
    conditionGroupId: groupId,
    type: forJoin ? "joinGroups" : "conditionGroups",
  });
  const showPlaceholder =
    placeholderViaSlice || (isOver && !conditionGroupData?.fields?.length);

  return (
    <div
      ref={setNodeRef}
      className={`${classes.group} ${isTopLevel ? classes.topLevel : ""}`}
    >
      {/* Saved groups (with dateCreated) show their AND/OR + delete header */}
      {!!conditionGroupData?.dateCreated && (
        <GroupHeader
          ruleId={ruleId}
          forJoin={forJoin}
          groupId={groupId}
          operator={conditionGroupData?.operator}
          canEdit={canEdit}
          onRequestDelete={() => setDeleteOpened(true)}
        />
      )}

      <Stack gap={0}>
        {conditionGroupData?.fields?.map((field, i) => {
          const isLastField = conditionGroupData.fields.length === i + 1;
          const lastNested =
            conditionGroupData?.groups?.[
              conditionGroupData.groups.length - 1
            ];
          // Stable key on field.id: hover state is external (in the slice),
          // so we no longer need to stringify the field to force remounts.
          return (
            <Condition
              key={field.id}
              ruleId={ruleId}
              queryId={queryId}
              groupId={groupId}
              forJoin={forJoin}
              fieldsData={field}
              withNestedPlaceholder={lastNested === "placeholder" && isLastField}
              canEdit={canEdit}
            />
          );
        })}
        {showPlaceholder && <Placeholder />}
      </Stack>

      {/* Visible drop zone when the group is empty — without this the
          drop target is invisible and users can't tell where to drop. */}
      {isTopLevel &&
        !conditionGroupData?.fields?.length &&
        !showPlaceholder && (
          <div className={classes.dropHint}>
            Drop an entity field here to add a condition
          </div>
        )}

      {conditionGroupData?.groups?.map((g, i) =>
        g === "placeholder" ? null : (
          <ConditionGroup
            key={g.id ?? i}
            ruleId={ruleId}
            queryId={queryId}
            forJoin={forJoin}
            conditionGroupData={g}
            canEdit={canEdit}
          />
        ),
      )}

      <DeleteConfirmModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        onDelete={() => setDeleteOpened(false)}
        label="Are you sure?"
        // Note: actual deletion is in GroupHeader so it can call the mutation
      />
    </div>
  );
}

function GroupHeader({
  ruleId,
  forJoin,
  groupId,
  operator,
  canEdit,
  onRequestDelete,
}) {
  const { t } = useTranslation();
  const { mutate: editGroup } = useEditGroupMutation({ ruleId });
  const { mutate: deleteGroup } = useDeleteGroupMutation({ ruleId });
  const [deleteOpened, setDeleteOpened] = useState(false);

  const matched = AND_OR_ITEMS.find(
    (item) => item.toLowerCase() === operator?.toLowerCase(),
  );
  const defaultValue = matched ?? AND_OR_ITEMS[0];

  return (
    <Group justify="flex-end" gap="xs">
      <Select
        data={AND_OR_ITEMS}
        value={defaultValue}
        onChange={(v) => {
          if (v == null) return;
          editGroup({ groupOperator: v.toUpperCase(), id: groupId, forJoin });
        }}
        disabled={!canEdit}
        variant="unstyled"
        w={80}
        allowDeselect={false}
        className={classes.conditionGroupSelect}
      />
      {canEdit && (
        <>
          <Tooltip label={t("conditionGroup.deleteGroup")} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Delete group"
              onClick={() => setDeleteOpened(true)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
          <DeleteConfirmModal
            opened={deleteOpened}
            onClose={() => setDeleteOpened(false)}
            onDelete={() => {
              deleteGroup(groupId);
              setDeleteOpened(false);
              onRequestDelete?.();
            }}
            label={t("conditionGroup.deleteGroup")}
          />
        </>
      )}
    </Group>
  );
}
